import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '../../environments/environment';

export interface MerchantDto {
	id: string;
	worldId: string;
	npcId: string | null;
	npcName: string | null;
	poiId: string | null;
	poiName: string | null;
	factionId: string | null;
	factionName: string | null;
	regionName: string | null;
	name: string;
	description: string | null;
	gmNotes: string | null;
	imageAssetId: string | null;
	foundryId: string | null;
	inventoryCount: number;
	createdAt: string;
	updatedAt: string;
}

export interface MerchantInventoryDto {
	merchantId: string;
	goodsId: string;
	goodsName: string | null;
	quantity: number | null;
	priceOverride: string | null;
	notes: string | null;
}

export interface MerchantsListResponse {
	merchants: MerchantDto[];
	total: number;
}

export interface MerchantGetResponse {
	merchant: MerchantDto;
	inventory: MerchantInventoryDto[];
}

export interface MerchantInventoryItemRequest {
	goodsId: string;
	quantity?: number | null;
	priceOverride?: string | null;
	notes?: string | null;
}

export interface UpsertMerchantRequest {
	name: string;
	description?: string | null;
	gmNotes?: string | null;
	imageAssetId?: string | null;
	npcId?: string | null;
	poiId?: string | null;
	factionId?: string | null;
	foundryId?: string | null;
	inventory: MerchantInventoryItemRequest[];
}

@Injectable({ providedIn: 'root' })
export class MerchantsService {
	constructor(private readonly http: HttpClient) {}

	list(
		worldId: string,
		args?: { q?: string; regionId?: string; poiId?: string; npcId?: string; factionId?: string }
	): Observable<MerchantsListResponse> {
		let params = new HttpParams().set('format', 'list');
		if (args?.q) params = params.set('q', args.q);
		if (args?.regionId) params = params.set('regionId', args.regionId);
		if (args?.poiId) params = params.set('poiId', args.poiId);
		if (args?.npcId) params = params.set('npcId', args.npcId);
		if (args?.factionId) params = params.set('factionId', args.factionId);
		return this.http.get<MerchantsListResponse>(`${environment.api.baseUrl}/v1/worlds/${worldId}/merchants`, { params });
	}

	get(worldId: string, merchantId: string): Observable<MerchantGetResponse> {
		return this.http.get<MerchantGetResponse>(`${environment.api.baseUrl}/v1/worlds/${worldId}/merchants/${merchantId}`);
	}

	create(worldId: string, request: UpsertMerchantRequest): Observable<{ merchant: MerchantDto }> {
		return this.http.post<{ merchant: MerchantDto }>(`${environment.api.baseUrl}/v1/worlds/${worldId}/merchants`, {
			Name: request.name,
			Description: request.description ?? null,
			GmNotes: request.gmNotes ?? null,
			ImageAssetId: request.imageAssetId ?? null,
			NpcId: request.npcId ?? null,
			PoiId: request.poiId ?? null,
			FactionId: request.factionId ?? null,
			FoundryId: request.foundryId ?? null,
			Inventory: (request.inventory ?? []).map((i) => ({
				GoodsId: i.goodsId,
				Quantity: i.quantity ?? null,
				PriceOverride: i.priceOverride ?? null,
				Notes: i.notes ?? null,
			})),
		});
	}

	update(worldId: string, merchantId: string, request: UpsertMerchantRequest): Observable<{ merchant: MerchantDto }> {
		return this.http.put<{ merchant: MerchantDto }>(`${environment.api.baseUrl}/v1/worlds/${worldId}/merchants/${merchantId}`, {
			Name: request.name,
			Description: request.description ?? null,
			GmNotes: request.gmNotes ?? null,
			ImageAssetId: request.imageAssetId ?? null,
			NpcId: request.npcId ?? null,
			PoiId: request.poiId ?? null,
			FactionId: request.factionId ?? null,
			FoundryId: request.foundryId ?? null,
			Inventory: (request.inventory ?? []).map((i) => ({
				GoodsId: i.goodsId,
				Quantity: i.quantity ?? null,
				PriceOverride: i.priceOverride ?? null,
				Notes: i.notes ?? null,
			})),
		});
	}

	delete(worldId: string, merchantId: string): Observable<void> {
		return this.http.delete<void>(`${environment.api.baseUrl}/v1/worlds/${worldId}/merchants/${merchantId}`);
	}
}
